import { useEffect, useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Field';
import { Skeleton } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Field';
import { useAdminSiteSettings, useUpdateSiteSettings } from '@/lib/admin-queries';
import { toast } from '@/components/ui/Toast';
import type { SiteSettings } from '@/lib/site-settings-default-types';
import { defaultSettings } from '@/lib/site-settings-default';

export function SiteSettingsPage() {
  const { data: row, isLoading } = useAdminSiteSettings();
  const update = useUpdateSiteSettings();
  const [v, setV] = useState<SiteSettings>(defaultSettings);

  useEffect(() => {
    if (row?.data) {
      setV({ ...defaultSettings, ...(row.data as Partial<SiteSettings>) });
    }
  }, [row]);

  function patchNested<K extends keyof SiteSettings>(top: K, key: string, val: unknown) {
    setV((cur) => ({ ...cur, [top]: { ...(cur[top] as Record<string, unknown>), [key]: val } }));
  }

  async function onSave() {
    try {
      await update.mutateAsync(v as unknown as Record<string, unknown>);
      toast.success('Site settings saved');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Site</p>
        <h1 className="mt-2 display-serif text-display-l text-fg">Brand & content</h1>
        <p className="mt-2 text-sm text-fg-muted">Brand name, contact info, navigation, social URLs, footer copy, SEO defaults.</p>
      </header>

      <Card className="p-6">
        <Tabs defaultValue="brand" orientation="horizontal">
          <TabsList className="mb-4">
            <TabsTrigger value="brand">Brand</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="social">Social</TabsTrigger>
            <TabsTrigger value="nav">Nav</TabsTrigger>
            <TabsTrigger value="footer">Footer</TabsTrigger>
            <TabsTrigger value="seo">SEO</TabsTrigger>
          </TabsList>

          <TabsContent value="brand">
            <div className="space-y-4">
              <Field id="brand.name" label="Name">
                {({ id }) => (
                  <Input id={id} value={v.brand.name} onChange={(e) => patchNested('brand', 'name', e.target.value)} />
                )}
              </Field>
              <Field id="brand.legal_name" label="Legal name">
                {({ id }) => (
                  <Input id={id} value={v.brand.legal_name} onChange={(e) => patchNested('brand', 'legal_name', e.target.value)} />
                )}
              </Field>
              <Field id="brand.tagline" label="Tagline">
                {({ id }) => (
                  <Textarea id={id} rows={2} value={v.brand.tagline} onChange={(e) => patchNested('brand', 'tagline', e.target.value)} />
                )}
              </Field>
            </div>
          </TabsContent>

          <TabsContent value="contact">
            <div className="space-y-4">
              <Field id="contact.email" label="Email">
                {({ id }) => <Input id={id} type="email" value={v.contact.email} onChange={(e) => patchNested('contact', 'email', e.target.value)} />}
              </Field>
              <Field id="contact.phone" label="Phone">
                {({ id }) => <Input id={id} value={v.contact.phone} onChange={(e) => patchNested('contact', 'phone', e.target.value)} />}
              </Field>
              <Field id="contact.address_line" label="Address">
                {({ id }) => <Input id={id} value={v.contact.address_line} onChange={(e) => patchNested('contact', 'address_line', e.target.value)} />}
              </Field>
              <Field id="contact.business_hours" label="Business hours">
                {({ id }) => <Input id={id} value={v.contact.business_hours} onChange={(e) => patchNested('contact', 'business_hours', e.target.value)} />}
              </Field>
            </div>
          </TabsContent>

          <TabsContent value="social">
            <div className="space-y-4">
              {(['linkedin', 'github', 'x', 'youtube', 'instagram', 'tiktok', 'facebook'] as const).map((k) => (
                <Field key={k} id={`social.${k}`} label={k.charAt(0).toUpperCase() + k.slice(1) + ' URL'} hint="Leave blank to hide">
                  {({ id }) => (
                    <Input
                      id={id}
                      type="url"
                      value={(v.social[k] as string | null) ?? ''}
                      onChange={(e) => patchNested('social', k, e.target.value || null)}
                      placeholder="https://"
                    />
                  )}
                </Field>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="nav">
            <p className="text-sm text-fg-muted">
              The primary navigation. Each entry has a label and a route. Drag-to-reorder is on the roadmap.
            </p>
            <pre className="mt-4 overflow-x-auto rounded-md border border-border bg-bg p-4 text-xs text-fg-muted">
              {JSON.stringify(v.nav, null, 2)}
            </pre>
            <p className="mt-2 text-xs text-fg-subtle">Edit in JSON via the Supabase Studio for now.</p>
          </TabsContent>

          <TabsContent value="footer">
            <div className="space-y-4">
              <Field id="footer.copyright" label="Copyright line" hint="Supports {'{year}'} substitution.">
                {({ id }) => (
                  <Input
                    id={id}
                    value={v.footer.copyright}
                    onChange={(e) => patchNested('footer', 'copyright', e.target.value)}
                  />
                )}
              </Field>
            </div>
          </TabsContent>

          <TabsContent value="seo">
            <div className="space-y-4">
              <Field id="seo.default_title_suffix" label="Default title suffix" hint="Appended to every page title. ' — Vonlof'">
                {({ id }) => (
                  <Input
                    id={id}
                    value={v.seo.default_title_suffix}
                    onChange={(e) => patchNested('seo', 'default_title_suffix', e.target.value)}
                  />
                )}
              </Field>
              <Field id="seo.default_description" label="Default description">
                {({ id }) => (
                  <Textarea
                    id={id}
                    rows={3}
                    value={v.seo.default_description}
                    onChange={(e) => patchNested('seo', 'default_description', e.target.value)}
                  />
                )}
              </Field>
              <Field id="seo.default_og_image_url" label="Default OG image URL">
                {({ id }) => (
                  <Input
                    id={id}
                    value={v.seo.default_og_image_url ?? ''}
                    onChange={(e) => patchNested('seo', 'default_og_image_url', e.target.value || null)}
                  />
                )}
              </Field>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-6 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button onClick={onSave} loading={update.isPending}>
            Save changes
          </Button>
        </div>
      </Card>
    </div>
  );
}